import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

const OrderTracker = ({ orderIds, onClose, restaurantId }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Rating State
  const [ratingOrder, setRatingOrder] = useState(null); // The order being rated
  const [ratingItem, setRatingItem] = useState(null);   // The specific item being rated
  const [ratingValue, setRatingValue] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // 1. Fetch all orders in the list
    const fetchOrders = async () => {
      const { data } = await supabase.from('orders').select('*').in('id', orderIds).order('created_at', { ascending: false });
      if (data) setOrders(data);
      setLoading(false);
    };

    fetchOrders();

    // 2. Realtime listener for ANY of these orders
    const channel = supabase
      .channel('track-my-orders')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'orders' }, (payload) => {
        if (orderIds.includes(payload.new.id)) {
          setOrders(prev => prev.map(o => o.id === payload.new.id ? payload.new : o));
        }
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [orderIds]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Accepted': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'Cooking': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'Ready': return 'text-green-600 bg-green-50 border-green-200';
      case 'Rejected': return 'text-red-600 bg-red-50 border-red-200';
      case 'Completed': return 'text-gray-600 bg-gray-100 border-gray-200';
      default: return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
  };

  const openRatingModal = (order, item) => {
    setRatingOrder(order);
    setRatingItem(item);
    setRatingValue(5);
    setComment("");
  };

  const submitRating = async () => {
    if (!ratingItem || !ratingOrder) return;
    setSubmitting(true);

    try {
      const { error } = await supabase.from('reviews').insert([{
        restaurant_id: restaurantId,
        menu_item_id: ratingItem.id, // We need to store item ID in order items JSON or fetch it. 
        // NOTE: Order items JSON usually stores {id, name, price...}. 
        // If it doesn't have ID, we might have an issue. Assuming it does based on standard cart logic.
        order_id: ratingOrder.id,
        rating: ratingValue,
        comment: comment,
        customer_name: "Customer", // Could ask for name
        is_verified: true
      }]);

      if (error) throw error;

      alert("Thanks for your feedback!");
      setRatingOrder(null);
      setRatingItem(null);
    } catch (err) {
      console.error("Error submitting review:", err);
      alert(`Failed to submit review: ${err.message || JSON.stringify(err)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center animate-fade-in">
      <div className="bg-white w-full max-w-md rounded-t-2xl shadow-2xl p-6 h-[70vh] flex flex-col animate-slide-up">

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2 text-black">
            🧾 My Orders <span className="bg-black text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">{orders.length}</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 font-bold hover:text-black">Close ✕</button>
        </div>

        {/* RATING MODAL OVERLAY */}
        {ratingItem && (
          <div className="absolute inset-0 bg-white z-50 p-6 flex flex-col items-center justify-center text-center">
            <h3 className="text-2xl font-bold mb-2">Rate {ratingItem.name}</h3>
            <p className="text-gray-500 mb-6 font-medium">How was your meal?</p>

            <div className="flex gap-2 mb-6">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setRatingValue(star)}
                  className={`text-4xl transition-transform hover:scale-110 ${star <= ratingValue ? 'text-yellow-400' : 'text-gray-200'}`}
                >
                  ★
                </button>
              ))}
            </div>

            <textarea
              className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6 focus:ring-2 focus:ring-black outline-none resize-none h-32"
              placeholder="Write a review (optional)..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />

            <div className="flex gap-3 w-full">
              <button onClick={() => setRatingItem(null)} className="flex-1 py-3 font-bold text-gray-500 hover:bg-gray-50 rounded-xl">Cancel</button>
              <button onClick={submitRating} disabled={submitting} className="flex-1 py-3 bg-black text-white font-bold rounded-xl shadow-lg">
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {loading ? <div className="text-center text-gray-500 py-10">Loading Status...</div> :
            orders.map(order => (
              <div key={order.id} className="border border-gray-100 rounded-xl p-4 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="text-xs font-bold text-gray-400">Order #{order.id}</span>
                    <p className="font-bold text-gray-900">₹{order.total_price}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-lg text-xs font-bold border ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </div>

                {/* Status Note */}
                {order.status_note && (
                  <div className="mb-3 bg-gray-50 p-2 rounded text-xs text-gray-600 italic">
                    " {order.status_note} "
                  </div>
                )}

                <div className="space-y-3 mt-4 border-t border-gray-50 pt-3">
                  {order.items.map((item, i) => (
                    <div key={i} className="flex justify-between items-center text-sm text-gray-700">
                      <span className="font-medium">{item.quantity}x {item.name}</span>

                      {/* RATE BUTTON - Only if Completed */}
                      {order.status === 'Completed' && (
                        <button
                          onClick={() => openRatingModal(order, item)}
                          className="text-xs font-bold text-[#ee5b2b] bg-orange-50 px-2 py-1 rounded hover:bg-orange-100 transition-colors"
                        >
                          ★ Rate
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};

export default OrderTracker;